import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Send, CheckCircle2, Save, Link2, Tag, Image as ImageIcon, XCircle, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { T } from "@/lib/testIds";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [receiptBlob, setReceiptBlob] = useState(null);
  const [receiptTitle, setReceiptTitle] = useState("");

  const load = async () => {
    try {
      const [c, r, p] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/reminders`, { params: { limit: 200 } }),
        api.get(`/payments`, { params: { limit: 200 } }),
      ]);
      setClient(c.data);
      setForm({ ...c.data, segmentsStr: (c.data.segments || []).join(", ") });
      setLogs((r.data || []).filter((l) => l.client_id === id));
      setPayments((p.data || []).filter((x) => x.client_id === id));
    } catch (e) {
      toast.error("Failed to load client");
      nav("/app/clients");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!client || !form) {
    return <div className="p-10 text-sm text-[#52525B]">Loading…</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        product_name: form.product_name,
        amount: parseFloat(form.amount),
        billing_cycle: form.billing_cycle,
        next_due_date: form.next_due_date,
        notes: form.notes || "",
        segments: (form.segmentsStr || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        active: form.active,
      };
      const r = await api.patch(`/clients/${id}`, payload);
      setClient(r.data);
      setForm({ ...r.data, segmentsStr: (r.data.segments || []).join(", ") });
      toast.success("Client updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  // One-click retag — toggles a single payment option on the client and saves immediately.
  const togglePaymentOption = async (optId) => {
    const current = new Set(client.payment_option_ids || []);
    if (current.has(optId)) current.delete(optId); else current.add(optId);
    const next = Array.from(current);
    setRetagging(true);
    try {
      const r = await api.patch(`/clients/${id}`, { payment_option_ids: next });
      setClient(r.data);
      setForm((f) => ({ ...f, payment_option_ids: r.data.payment_option_ids }));
      toast.success(next.length ? "Payment options updated" : "Cleared — showing all options");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not retag");
    } finally {
      setRetagging(false);
    }
  };

  const copyPortalLink = async () => {
    const url = `${window.location.origin}/portal/${client.portal_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied");
    } catch {
      toast.info(url);
    }
  };

  const sendReminder = async () => {
    try {
      const r = await api.post(`/reminders/send/${id}`);
      toast.success(`Sent ${r.data.count} reminder(s)`);
      await load();
    } catch (e) {
      toast.error("Send failed");
    }
  };

  const markPaid = async () => {
    try {
      await api.post(`/clients/${id}/mark-paid`, { method: "manual" });
      toast.success("Marked as paid");
      await load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const viewReceipt = async (payment) => {
    try {
      const r = await api.get(`/payments/${payment.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setReceiptBlob(url);
      setReceiptTitle(payment.receipt_original_name || `Receipt · ${payment.reference || "self-reported"}`);
    } catch (e) {
      toast.error("Could not load receipt");
    }
  };

  const closeReceipt = () => {
    if (receiptBlob) URL.revokeObjectURL(receiptBlob);
    setReceiptBlob(null);
  };

  const verifyPayment = async (payment) => {
    try {
      await api.post(`/payments/${payment.id}/verify`);
      toast.success("Payment verified");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    }
  };

  const rejectPayment = async (payment) => {
    if (!window.confirm("Reject this self-reported payment?")) return;
    try {
      await api.post(`/payments/${payment.id}/reject`);
      toast.success("Payment rejected");
      await load();
    } catch (e) {
      toast.error("Reject failed");
    }
  };

  const statusCls =
    client.payment_status === "paid" ? "co-pill co-pill-paid" :
    client.payment_status === "overdue" ? "co-pill co-pill-overdue" : "co-pill co-pill-pending";

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/app/clients" className="p-2 hover:bg-[#F4F4F5] rounded-sm">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          </Link>
          <div>
            <div className="co-label">Client · {client.billing_cycle} plan</div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">
              {client.name}
            </h1>
          </div>
          <span className={statusCls}>{client.payment_status.toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="detail-send-reminder"
            onClick={sendReminder}
            className="inline-flex items-center gap-2 border border-[#0A0A0B] px-4 py-2 text-sm font-semibold rounded-sm hover:bg-[#F4F4F5] co-btn-press"
          >
            <Send className="h-4 w-4" strokeWidth={1.75} /> Send reminder
          </button>
          {client.payment_status !== "paid" && (
            <button
              data-testid="detail-mark-paid"
              onClick={markPaid}
              className="inline-flex items-center gap-2 bg-[#15803D] hover:bg-[#166534] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> Mark paid
            </button>
          )}
        </div>
      </header>

      <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-3 gap-0 border co-hairline bg-white">
        <div className="lg:col-span-2 p-6 lg:p-8 lg:border-r co-hairline">
          <div className="co-label mb-4">Client details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="co-label">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2" /></div>
            <div><Label className="co-label">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2" /></div>
            <div className="sm:col-span-2"><Label className="co-label">Email</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-2" /></div>
            <div><Label className="co-label">Product</Label><Input value={form.product_name} onChange={(e) => set("product_name", e.target.value)} className="mt-2" /></div>
            <div><Label className="co-label">Amount</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-2" /></div>
            <div>
              <Label className="co-label">Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => set("billing_cycle", v)}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="co-label">Next due</Label><Input type="date" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} className="mt-2" /></div>
            <div className="sm:col-span-2"><Label className="co-label">Notes</Label><Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-2" /></div>
            <div className="sm:col-span-2">
              <Label className="co-label">Segments</Label>
              <Input
                value={form.segmentsStr || ""}
                onChange={(e) => set("segmentsStr", e.target.value)}
                className="mt-2"
                placeholder="e.g. gym-premium, vip"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(form.segmentsStr || "").split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 border border-[#C7D2FE] bg-[#ECECFE] text-[#4338CA] rounded-sm">
                    <Tag className="h-3 w-3" strokeWidth={2} /> {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={saving} data-testid="detail-save" className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200">
              <Save className="h-4 w-4" strokeWidth={2} /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={copyPortalLink}
              data-testid="detail-copy-portal"
              className="inline-flex items-center gap-2 border border-[#0A0A0B] px-4 py-2 text-sm font-semibold rounded-sm hover:bg-[#F4F4F5] co-btn-press transition-colors duration-200"
            >
              <Link2 className="h-4 w-4" strokeWidth={1.75} /> Copy portal link
            </button>
          </div>
        </div>

        <div className="p-6 lg:p-8">
          <div className="co-label mb-4">Summary</div>
          <div className="space-y-3">
            <div><div className="text-xs text-[#52525B]">Amount / cycle</div><div className="font-heading text-2xl font-bold tracking-tight">{fmtINR(client.amount)}</div></div>
            <div><div className="text-xs text-[#52525B]">Next due</div><div className="font-mono text-sm">{client.next_due_date}</div></div>
            <div><div className="text-xs text-[#52525B]">Last paid</div><div className="font-mono text-sm">{client.last_paid_date || "—"}</div></div>
          </div>

          <div className="co-label mt-8 mb-3">Reminder history</div>
          {logs.length === 0 ? (
            <div className="text-sm text-[#52525B]">No reminders sent yet.</div>
          ) : (
            <ul className="space-y-2 max-h-[200px] overflow-auto pr-2">
              {logs.map((l) => (
                <li key={l.id} className="text-xs border-b co-hairline pb-2">
                  <div className="flex items-center gap-2">
                    <span className={`co-pill ${l.status === "sent" ? "co-pill-sent" : l.status === "failed" ? "co-pill-failed" : "co-pill-mock"}`}>
                      {l.channel.toUpperCase()} · {l.status.toUpperCase()}
                    </span>
                    <span className="text-[#52525B]">{new Date(l.sent_at).toLocaleString()}</span>
                  </div>
                  {l.error && <div className="mt-1 text-[#B91C1C]">{l.error}</div>}
                </li>
              ))}
            </ul>
          )}

          <div className="co-label mt-8 mb-3">Payments</div>
          {payments.length === 0 ? (
            <div className="text-sm text-[#52525B]">No payments recorded yet.</div>
          ) : (
            <ul className="space-y-3">
              {payments.map((p) => (
                <li key={p.id} className="border-b co-hairline pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">
                        {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(p.amount)}
                      </div>
                      <div className="text-xs text-[#52525B]">
                        {p.method.replace("_", " ")} · {new Date(p.created_at).toLocaleDateString()}
                      </div>
                      {p.reference && (
                        <div className="text-xs text-[#52525B] font-mono truncate max-w-[200px]">
                          ref: {p.reference}
                        </div>
                      )}
                    </div>
                    <span className={
                      p.status === "success" ? "co-pill co-pill-paid" :
                      p.status === "rejected" ? "co-pill co-pill-failed" :
                      "co-pill co-pill-mock"
                    }>
                      {p.status.replace("_", " ").toUpperCase()}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {p.receipt_path && (
                      <button
                        onClick={() => viewReceipt(p)}
                        data-testid={`${T.viewReceiptBtn}-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 border border-[#0A0A0B] rounded-sm hover:bg-[#F4F4F5] co-btn-press transition-colors duration-200"
                      >
                        <ImageIcon className="h-3 w-3" strokeWidth={2} /> View receipt
                      </button>
                    )}
                    {p.status === "pending_review" && (
                      <>
                        <button
                          onClick={() => verifyPayment(p)}
                          data-testid={`${T.verifyPaymentBtn}-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-[#15803D] hover:bg-[#166534] text-white rounded-sm co-btn-press transition-colors duration-200"
                        >
                          <Check className="h-3 w-3" strokeWidth={2} /> Verify
                        </button>
                        <button
                          onClick={() => rejectPayment(p)}
                          data-testid={`${T.rejectPaymentBtn}-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 border border-[#B91C1C] text-[#B91C1C] rounded-sm hover:bg-[#FEF2F2] co-btn-press transition-colors duration-200"
                        >
                          <XCircle className="h-3 w-3" strokeWidth={2} /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={!!receiptBlob} onOpenChange={(v) => { if (!v) closeReceipt(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight truncate">{receiptTitle}</DialogTitle>
          </DialogHeader>
          {receiptBlob && (
            <img
              src={receiptBlob}
              alt="Payment receipt"
              className="w-full max-h-[70vh] object-contain border co-hairline bg-[#FAFAFA]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
